import { useState } from 'react';
import {
  Dialog,
  Button,
  Input,
  Select,
  Option,
  TextArea,
  Label,
  FlexBox,
  MessageStrip
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import {
  processingApi,
  serviceProviderApi,
  tomApi,
  dsrApi,
  breachApi
} from '../services/api';
import {
  LegalBasis,
  DataSubjectCategory,
  DataCategory,
  ServiceProviderRole,
  TomCategory,
  TomStatus,
  DataSubjectRequestType,
  DataSubjectRequestChannel,
  BreachCategory,
  BreachSeverity
} from '@dsgvo-buddy/types';

interface QuickCreateDialogProps {
  type: 'processing' | 'service-provider' | 'tom' | 'dsr' | 'breach';
  organizationId: number;
  open: boolean;
  onClose: () => void;
  onCreated: (id: number, name: string) => void;
}

export default function QuickCreateDialog({ type, organizationId, open, onClose, onCreated }: QuickCreateDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Processing fields
  const [procName, setProcName] = useState('');
  const [procDepartment, setProcDepartment] = useState('');
  const [procPurpose, setProcPurpose] = useState('');
  const [procLegalBasis, setProcLegalBasis] = useState<LegalBasis>(LegalBasis.CONTRACT);

  // Service Provider fields
  const [spName, setSpName] = useState('');
  const [spRole, setSpRole] = useState<ServiceProviderRole>(ServiceProviderRole.PROCESSOR);
  const [spDescription, setSpDescription] = useState('');

  // TOM fields
  const [tomName, setTomName] = useState('');
  const [tomCategory, setTomCategory] = useState<TomCategory>(TomCategory.ACCESS_CONTROL);
  const [tomDescription, setTomDescription] = useState('');

  // DSR fields
  const [dsrName, setDsrName] = useState('');
  const [dsrEmail, setDsrEmail] = useState('');
  const [dsrType, setDsrType] = useState<DataSubjectRequestType>(DataSubjectRequestType.ACCESS);
  const [dsrChannel, setDsrChannel] = useState<DataSubjectRequestChannel>(DataSubjectRequestChannel.EMAIL);

  // Breach fields
  const [breachTitle, setBreachTitle] = useState('');
  const [breachCategory, setBreachCategory] = useState<BreachCategory>(BreachCategory.UNAUTHORIZED_ACCESS);
  const [breachSeverity, setBreachSeverity] = useState<BreachSeverity>(BreachSeverity.MEDIUM);

  const resetFields = () => {
    setError(null);
    setProcName(''); setProcDepartment(''); setProcPurpose(''); setProcLegalBasis(LegalBasis.CONTRACT);
    setSpName(''); setSpRole(ServiceProviderRole.PROCESSOR); setSpDescription('');
    setTomName(''); setTomCategory(TomCategory.ACCESS_CONTROL); setTomDescription('');
    setDsrName(''); setDsrEmail(''); setDsrType(DataSubjectRequestType.ACCESS); setDsrChannel(DataSubjectRequestChannel.EMAIL);
    setBreachTitle(''); setBreachCategory(BreachCategory.UNAUTHORIZED_ACCESS); setBreachSeverity(BreachSeverity.MEDIUM);
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    try {
      switch (type) {
        case 'processing': {
          if (!procName.trim() || !procDepartment.trim() || !procPurpose.trim()) {
            setError('Name, Abteilung und Zweck sind Pflichtfelder');
            setLoading(false);
            return;
          }
          const res = await processingApi.create({
            organization_id: organizationId,
            name: procName,
            department: procDepartment,
            purpose: procPurpose,
            legal_basis: procLegalBasis,
            data_subjects: [DataSubjectCategory.CUSTOMERS],
            data_categories: [DataCategory.CONTACT],
            recipients: [],
            retention_period: 36,
            third_country_transfer: false
          });
          if (res.success && res.data) {
            onCreated(res.data.id, res.data.name);
            handleClose();
          } else { setError(res.error || 'Fehler beim Erstellen'); }
          break;
        }
        case 'service-provider': {
          if (!spName.trim()) { setError('Name ist Pflichtfeld'); setLoading(false); return; }
          const res = await serviceProviderApi.create({
            organization_id: organizationId,
            name: spName,
            role: spRole,
            service_description: spDescription || undefined
          });
          if (res.success && res.data) {
            onCreated(res.data.id, res.data.name);
            handleClose();
          } else { setError(res.error || 'Fehler beim Erstellen'); }
          break;
        }
        case 'tom': {
          if (!tomName.trim()) { setError('Name ist Pflichtfeld'); setLoading(false); return; }
          const res = await tomApi.create({
            organization_id: organizationId,
            name: tomName,
            category: tomCategory,
            status: TomStatus.PLANNED,
            description: tomDescription || undefined
          });
          if (res.success && res.data) {
            onCreated(res.data.id, res.data.name);
            handleClose();
          } else { setError(res.error || 'Fehler beim Erstellen'); }
          break;
        }
        case 'dsr': {
          if (!dsrName.trim()) { setError('Name ist Pflichtfeld'); setLoading(false); return; }
          const res = await dsrApi.create({
            organization_id: organizationId,
            request_type: dsrType,
            channel: dsrChannel,
            subject_name: dsrName,
            subject_email: dsrEmail || undefined,
            received_at: new Date().toISOString().slice(0, 10)
          });
          if (res.success && res.data) {
            onCreated(res.data.id, dsrName);
            handleClose();
          } else { setError(res.error || 'Fehler beim Erstellen'); }
          break;
        }
        case 'breach': {
          if (!breachTitle.trim()) { setError('Titel ist Pflichtfeld'); setLoading(false); return; }
          const now = new Date().toISOString();
          const res = await breachApi.create({
            organization_id: organizationId,
            title: breachTitle,
            category: breachCategory,
            severity: breachSeverity,
            incident_at: now,
            detected_at: now
          });
          if (res.success && res.data) {
            onCreated(res.data.id, res.data.title);
            handleClose();
          } else { setError(res.error || 'Fehler beim Erstellen'); }
          break;
        }
      }
    } catch {
      setError('Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  const titles: Record<string, string> = {
    'processing': 'Neue Verarbeitung (Schnellerfassung)',
    'service-provider': 'Neuer Dienstleister (Schnellerfassung)',
    'tom': 'Neue TOM-Maßnahme (Schnellerfassung)',
    'dsr': 'Neue Betroffenenanfrage (Schnellerfassung)',
    'breach': 'Neuen Vorfall melden (Schnellerfassung)'
  };

  const legalBasisOptions: Record<string, string> = {
    [LegalBasis.CONSENT]: 'Einwilligung',
    [LegalBasis.CONTRACT]: 'Vertragserfüllung',
    [LegalBasis.LEGAL_OBLIGATION]: 'Rechtliche Verpflichtung',
    [LegalBasis.LEGITIMATE_INTEREST]: 'Berechtigtes Interesse'
  };

  const spRoleOptions: Record<string, string> = {
    [ServiceProviderRole.PROCESSOR]: 'Auftragsverarbeiter',
    [ServiceProviderRole.JOINT_CONTROLLER]: 'Gem. Verantwortlicher',
    [ServiceProviderRole.RECIPIENT]: 'Empfänger'
  };

  const tomCategoryOptions: Record<string, string> = {
    [TomCategory.ACCESS_CONTROL]: 'Zugriffskontrolle',
    [TomCategory.ENCRYPTION]: 'Verschlüsselung',
    [TomCategory.AVAILABILITY]: 'Verfügbarkeit',
    [TomCategory.RECOVERY]: 'Wiederherstellung',
    [TomCategory.SEPARATION]: 'Trennbarkeit',
    [TomCategory.DELETION]: 'Löschung',
    [TomCategory.PRIVACY_BY_DESIGN]: 'Privacy by Design'
  };

  const dsrTypeOptions: Record<string, string> = {
    [DataSubjectRequestType.ACCESS]: 'Auskunft (Art. 15)',
    [DataSubjectRequestType.ERASURE]: 'Löschung (Art. 17)',
    [DataSubjectRequestType.RECTIFICATION]: 'Berichtigung (Art. 16)',
    [DataSubjectRequestType.RESTRICTION]: 'Einschränkung (Art. 18)',
    [DataSubjectRequestType.PORTABILITY]: 'Datenübertragbarkeit (Art. 20)',
    [DataSubjectRequestType.OBJECTION]: 'Widerspruch (Art. 21)'
  };

  const breachCategoryOptions: Record<string, string> = {
    [BreachCategory.UNAUTHORIZED_ACCESS]: 'Unbefugter Zugriff',
    [BreachCategory.DATA_LOSS]: 'Datenverlust',
    [BreachCategory.DEVICE_LOSS]: 'Geräteverlust',
    [BreachCategory.RANSOMWARE]: 'Ransomware',
    [BreachCategory.MISCONFIGURATION]: 'Fehlkonfiguration',
    [BreachCategory.OTHER]: 'Sonstiges'
  };

  return (
    <Dialog
      open={open}
      onAfterClose={handleClose}
      headerText={titles[type]}
      footer={
        <FlexBox justifyContent="End" style={{ width: '100%', padding: '0.5rem' }}>
          <Button design="Transparent" onClick={handleClose} disabled={loading} style={{ marginRight: '0.5rem' }}>
            Abbrechen
          </Button>
          <Button design="Emphasized" onClick={handleCreate} disabled={loading}>
            {loading ? 'Wird erstellt...' : 'Erstellen'}
          </Button>
        </FlexBox>
      }
    >
      <div style={{ padding: '1rem', minWidth: '450px', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {error && (
          <MessageStrip design="Negative" hideCloseButton>{error}</MessageStrip>
        )}

        <MessageStrip design="Information" hideCloseButton style={{ fontSize: '0.8125rem' }}>
          Schnellerfassung mit Pflichtfeldern. Weitere Details können anschließend auf der Detailseite ergänzt werden.
        </MessageStrip>

        {type === 'processing' && (
          <>
            <div>
              <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Name</Label>
              <Input value={procName} onInput={(e: any) => setProcName(e.target.value)} placeholder="z.B. Kundenverwaltung" style={{ width: '100%' }} />
            </div>
            <div>
              <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Abteilung</Label>
              <Input value={procDepartment} onInput={(e: any) => setProcDepartment(e.target.value)} placeholder="z.B. Vertrieb" style={{ width: '100%' }} />
            </div>
            <div>
              <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Zweck</Label>
              <Input value={procPurpose} onInput={(e: any) => setProcPurpose(e.target.value)} placeholder="z.B. Verwaltung von Kundendaten" style={{ width: '100%' }} />
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Rechtsgrundlage</Label>
              <Select onChange={(e: any) => setProcLegalBasis(e.detail.selectedOption.value)} style={{ width: '100%' }}>
                {Object.entries(legalBasisOptions).map(([k, v]) => (
                  <Option key={k} value={k} selected={k === procLegalBasis}>{v}</Option>
                ))}
              </Select>
            </div>
          </>
        )}

        {type === 'service-provider' && (
          <>
            <div>
              <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Name</Label>
              <Input value={spName} onInput={(e: any) => setSpName(e.target.value)} placeholder="z.B. Cloud-Anbieter GmbH" style={{ width: '100%' }} />
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Rolle</Label>
              <Select onChange={(e: any) => setSpRole(e.detail.selectedOption.value)} style={{ width: '100%' }}>
                {Object.entries(spRoleOptions).map(([k, v]) => (
                  <Option key={k} value={k} selected={k === spRole}>{v}</Option>
                ))}
              </Select>
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Leistungsbeschreibung</Label>
              <TextArea value={spDescription} onInput={(e: any) => setSpDescription(e.target.value)} placeholder="Optional" rows={2} style={{ width: '100%' }} />
            </div>
          </>
        )}

        {type === 'tom' && (
          <>
            <div>
              <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Name der Maßnahme</Label>
              <Input value={tomName} onInput={(e: any) => setTomName(e.target.value)} placeholder="z.B. Zwei-Faktor-Authentifizierung" style={{ width: '100%' }} />
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Kategorie</Label>
              <Select onChange={(e: any) => setTomCategory(e.detail.selectedOption.value)} style={{ width: '100%' }}>
                {Object.entries(tomCategoryOptions).map(([k, v]) => (
                  <Option key={k} value={k} selected={k === tomCategory}>{v}</Option>
                ))}
              </Select>
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Beschreibung</Label>
              <TextArea value={tomDescription} onInput={(e: any) => setTomDescription(e.target.value)} placeholder="Optional" rows={2} style={{ width: '100%' }} />
            </div>
          </>
        )}

        {type === 'dsr' && (
          <>
            <div>
              <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Name der betroffenen Person</Label>
              <Input value={dsrName} onInput={(e: any) => setDsrName(e.target.value)} placeholder="Vor- und Nachname" style={{ width: '100%' }} />
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>E-Mail</Label>
              <Input value={dsrEmail} onInput={(e: any) => setDsrEmail(e.target.value)} placeholder="Optional" style={{ width: '100%' }} />
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Anfragetyp</Label>
              <Select onChange={(e: any) => setDsrType(e.detail.selectedOption.value)} style={{ width: '100%' }}>
                {Object.entries(dsrTypeOptions).map(([k, v]) => (
                  <Option key={k} value={k} selected={k === dsrType}>{v}</Option>
                ))}
              </Select>
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Eingangskanal</Label>
              <Select onChange={(e: any) => setDsrChannel(e.detail.selectedOption.value)} style={{ width: '100%' }}>
                <Option value="email" selected>E-Mail</Option>
                <Option value="letter">Brief</Option>
                <Option value="phone">Telefon</Option>
                <Option value="web_form">Webformular</Option>
                <Option value="other">Sonstiges</Option>
              </Select>
            </div>
          </>
        )}

        {type === 'breach' && (
          <>
            <div>
              <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Titel des Vorfalls</Label>
              <Input value={breachTitle} onInput={(e: any) => setBreachTitle(e.target.value)} placeholder="z.B. Unbefugter Zugriff auf Kundendatenbank" style={{ width: '100%' }} />
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Kategorie</Label>
              <Select onChange={(e: any) => setBreachCategory(e.detail.selectedOption.value)} style={{ width: '100%' }}>
                {Object.entries(breachCategoryOptions).map(([k, v]) => (
                  <Option key={k} value={k} selected={k === breachCategory}>{v}</Option>
                ))}
              </Select>
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Schweregrad</Label>
              <Select onChange={(e: any) => setBreachSeverity(e.detail.selectedOption.value)} style={{ width: '100%' }}>
                <Option value="low">Gering</Option>
                <Option value="medium" selected>Mittel</Option>
                <Option value="high">Hoch</Option>
                <Option value="critical">Kritisch</Option>
              </Select>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
